/**
 * "Add to playlist" sheet — the one place a song joins a playlist.
 *
 * Reached from the More menu on a library row and from the Studio's ACTIVE SONG
 * panel, which is what §12's "add to playlist from Library and Studio" asks for; both
 * raise it the same way every other dialog is raised, by setting a target on uiStore.
 *
 * Existing playlists and a name field for a new one, in one dialog rather than two.
 * The first time anyone opens this there are no playlists, so a dialog that only
 * listed them would be an empty box with a dead end in it — and splitting "add" from
 * "create and add" would make the common first action two dialogs deep.
 *
 * A playlist that already holds the song is shown as such and is NOT disabled.
 * Disabling it would leave the reason to a colour, and pressing it toasts "already
 * in" — which answers the question the press was asking.
 */
import { useState } from 'react';
import { Check, ListMusic, Plus, X } from 'lucide-react';
import { Button, Modal } from '../ui';
import { TextField } from '../forms';
import { c } from '../../styles/theme';
import type { Playlist, Song } from '../../types/domain';

export interface AddToPlaylistModalProps {
  /** The song being added, or null when closed. */
  song: Song | null;
  /** Every playlist, newest first. */
  playlists: Playlist[];
  /** Ids of the playlists that already contain the song. */
  containingIds: string[];
  /** "3 tracks · 9:45" for a playlist row. */
  summaryFor: (playlist: Playlist) => string;
  onAdd: (playlist: Playlist) => void;
  onCreate: (name: string) => void;
  onClose: () => void;
}

function AddToPlaylistBody({
  song,
  playlists,
  containingIds,
  summaryFor,
  onAdd,
  onCreate,
  onClose,
}: AddToPlaylistModalProps & { song: Song }) {
  const [name, setName] = useState('');
  const trimmed = name.trim();

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 10,
          marginBottom: 14,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <p style={{ fontWeight: 600, fontSize: 15, margin: 0, fontFamily: 'Space Grotesk' }}>
            Add to playlist
          </p>
          {/* The title is the confirmation that the right row's menu was used. */}
          <p
            style={{
              fontSize: 12,
              color: c.text2,
              margin: '4px 0 0',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {song.title}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          style={{ background: 'none', border: 'none', cursor: 'pointer', flexShrink: 0 }}
          aria-label="Close"
        >
          <X size={16} color={c.text2} />
        </button>
      </div>

      {playlists.length > 0 && (
        /*
          Scrolled at a fixed height rather than growing: a user with thirty playlists
          would otherwise get a dialog taller than the viewport, and the name field
          below — the way out when none of them is the right one — would be the part
          pushed off the screen.
        */
        <div
          className="lyx-playlist-picker"
          role="group"
          aria-label="Your playlists"
          style={{ marginBottom: 14 }}
        >
          {playlists.map((playlist) => {
            const has = containingIds.includes(playlist.id);
            return (
              <button
                key={playlist.id}
                type="button"
                className="lyx-playlist-pick"
                onClick={() => onAdd(playlist)}
                aria-label={`Add ${song.title} to ${playlist.name}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  width: '100%',
                  textAlign: 'left',
                  background: c.surface2,
                  border: `1px solid ${has ? c.accentBorder : c.border}`,
                  borderRadius: 8,
                  padding: '8px 10px',
                  cursor: 'pointer',
                  color: c.text1,
                  fontFamily: 'Inter',
                }}
              >
                <ListMusic size={14} color={has ? c.accent : c.text2} />
                <span style={{ minWidth: 0, flex: 1 }}>
                  <span
                    style={{
                      display: 'block',
                      fontSize: 13,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {playlist.name}
                  </span>
                  {/* text2, not text3. This line is read — it is how you tell two
                      playlists apart before adding to one — and text3 measures 2.84:1
                      on surface2, below even the 3:1 icon threshold. The size drop from
                      13 to 11 carries the hierarchy instead. See contrast.test.ts. */}
                  <span style={{ display: 'block', fontSize: 11, color: c.text2 }}>
                    {summaryFor(playlist)}
                  </span>
                </span>
                {has && (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      fontSize: 11,
                      color: c.accent,
                      flexShrink: 0,
                    }}
                  >
                    <Check size={12} />
                    Added
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* A real <form> around the name field and its button, so Enter creates the playlist
          — the same fix Login.tsx, NewProjectForm.tsx, Ticket.tsx and Playlists.tsx carry,
          with the same note: "a real <form> so Enter submits, which the prototype's div did
          not". Enter in a single text field inside a dialog is the strongest expectation
          there is, and here it did nothing.

          Only this pair, not the picker above: those rows are `type="button"` and add to an
          EXISTING playlist, which is a different action — putting them inside the form would
          not change their behaviour but would imply they share this submission.

          The guard repeats the button's `disabled` rather than trusting it: a disabled button
          does not block Enter-to-submit in a form, so an empty name would otherwise create an
          untitled playlist by keyboard while the button correctly refused it. */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!trimmed) return;
          onCreate(trimmed);
        }}
      >
        <TextField
          id="lyx-new-playlist-name"
          label={playlists.length > 0 ? 'Or create a new playlist' : 'New playlist name'}
          value={name}
          onChange={setName}
          placeholder="Late night drives"
          marginBottom={14}
          style={{ padding: '8px 10px' }}
        />

        <Button type="submit" full disabled={!trimmed}>
          <Plus size={13} />
          Create and add
        </Button>
      </form>
    </>
  );
}

export default function AddToPlaylistModal(props: AddToPlaylistModalProps) {
  const { song, onClose } = props;
  return (
    <Modal open={Boolean(song)} onClose={onClose} ariaLabel="Add to playlist" width={400}>
      {/* Keyed by id so the name field clears when a different song is opened. */}
      {song && <AddToPlaylistBody key={song.id} {...props} song={song} />}
    </Modal>
  );
}
